import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, FileText, Trash2 } from "lucide-react";

const DOC_TYPE_LABEL = { building_code: "Código de construcción", product_spec: "Ficha técnica", internal_guide: "Guía interna" };
const STATUS = {
  processing: { label: "Procesando", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  ready: { label: "Listo", cls: "bg-[#10B981]/10 text-[#10B981]" },
  failed: { label: "Falló", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
};

const EMPTY_FORM = { title: "", doc_type: "building_code", state: "", city: "", zip_codes: "", effective_year: "", file: null };

export default function KnowledgePage() {
  const { activeTenantId, activeTenant } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/knowledge/documents");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  useEffect(() => {
    const hasProcessing = items.some((i) => i.status === "processing");
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(load, 3000);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, load]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };

  const upload = async () => {
    if (!form.file) return toast.error("Elegí un archivo PDF");
    if (!form.title.trim()) return toast.error("Escribí un título");
    if (!form.state.trim()) return toast.error("Indicá el estado (jurisdicción), ej. NY");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("doc_type", form.doc_type);
    fd.append("state", form.state.toUpperCase());
    if (form.city) fd.append("city", form.city);
    const zips = form.zip_codes.split(",").map((z) => z.trim()).filter(Boolean);
    fd.append("zip_codes", JSON.stringify(zips));
    if (form.effective_year) fd.append("effective_year", form.effective_year);
    try {
      await api.post("/knowledge/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF subido. Procesando en background...");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el documento");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/knowledge/documents/${doc.id}`);
      toast.success("Documento eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Códigos de construcción, fichas técnicas y guías internas. El contenido generado SOLO cita
          valores técnicos (valor R, códigos) que estén acá — nunca del conocimiento general del modelo.
        </p>
        <div className="flex gap-2 shrink-0">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-knowledge-doc-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
                <Plus className="h-4 w-4 mr-1" /> Subir PDF
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="knowledge-upload-dialog">
              <DialogHeader><DialogTitle className="font-heading">Subir documento</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Archivo PDF</Label>
                  <input ref={fileRef} type="file" accept="application/pdf" data-testid="knowledge-file-input"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    className="text-sm w-full border border-[#E4E4E7] rounded-md p-2" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Título</Label>
                  <Input data-testid="knowledge-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="NY Energy Conservation Construction Code 2024" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Tipo de documento</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger data-testid="knowledge-doctype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOC_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Estado (jurisdicción)</Label>
                    <Input data-testid="knowledge-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NY" maxLength={2} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#71717A]">Año de vigencia</Label>
                    <Input data-testid="knowledge-year-input" type="number" value={form.effective_year} onChange={(e) => setForm({ ...form, effective_year: e.target.value })} placeholder="2024" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Ciudad (opcional)</Label>
                  <Input data-testid="knowledge-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Brooklyn" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#71717A]">Códigos postales que cubre (vacío = todo el estado)</Label>
                  <Input data-testid="knowledge-zips-input" value={form.zip_codes} onChange={(e) => setForm({ ...form, zip_codes: e.target.value })} placeholder="11201, 11215" />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="upload-knowledge-doc-button" onClick={upload}
                  disabled={uploading || activeTenant?.status === "suspended"}
                  title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
                  className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
                >
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Subir
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center px-6" data-testid="knowledge-empty-state">
            <FileText className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm font-medium text-[#09090B]">Todavía no subiste ningún documento</p>
            <p className="mt-1 text-xs text-[#71717A] max-w-sm">
              Los documentos técnicos (códigos de construcción, fichas técnicas) respaldan el contenido
              que cita datos duros -- valores R, normas -- para que se pueda publicar sin revisión manual.
            </p>
            <Button
              size="sm" className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              data-testid="knowledge-empty-state-button" onClick={openCreate}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Subir el primero
            </Button>
          </div>
        ) : (
          <Table data-testid="knowledge-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Título</TableHead>
                <TableHead>Jurisdicción</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Fragmentos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => {
                const s = STATUS[d.status] || STATUS.processing;
                return (
                  <TableRow key={d.id} data-testid={`knowledge-row-${d.id}`}>
                    <TableCell className="font-medium">{d.title}</TableCell>
                    <TableCell className="text-[#71717A]">
                      {d.state}{d.city ? `, ${d.city}` : ""} {d.zip_codes?.length ? `(${d.zip_codes.join(", ")})` : "(todo el estado)"}
                      {d.effective_year ? ` · ${d.effective_year}` : ""}
                    </TableCell>
                    <TableCell className="text-[#71717A]">{DOC_TYPE_LABEL[d.doc_type]}</TableCell>
                    <TableCell className="text-[#71717A]">{d.chunk_count || "-"}</TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${s.cls}`}>{s.label}</Badge>
                      {d.status === "processing" && <Progress value={d.processing_progress} className="h-1 w-24 mt-1" data-testid={`knowledge-progress-${d.id}`} />}
                      {d.status === "failed" && <p className="text-xs text-[#EF4444] mt-1 max-w-xs">{d.error_message}</p>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => remove(d)} data-testid={`delete-knowledge-${d.id}`} className="text-[#EF4444]">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
